// Customer and staff IoT requests use separate, memory-only sessions.
export const isIotPrototypeHost = (hostname) => ['admin.stg.airconrg.com', 'localhost', '127.0.0.1', '[::1]'].includes(hostname);
export const APP_METRICS = Object.freeze([
    { key: 'pm1_0', label: 'PM1.0', unit: '㎍/㎥' },
    { key: 'pm2_5', label: 'PM2.5', unit: '㎍/㎥' },
    { key: 'pm10', label: 'PM10', unit: '㎍/㎥' },
    { key: 'voc', label: 'VOC', unit: 'mg/㎥' },
    { key: 'co2', label: 'CO₂', unit: 'ppm' },
    { key: 'temperature', label: '온도', unit: '℃' },
    { key: 'humidity', label: '습도', unit: '%' }
]);
export const INITIAL_IR_STATE = Object.freeze({ power: 'OFF', mode: 'AUTO', fan: 'AUTO', temp: 24 });
export const TERMINAL_STATUSES = ['SUCCEEDED', 'FAILED', 'TIMED_OUT', 'REJECTED'];
export const COMMAND_LABELS = {
    PENDING: 'IR 전송을 준비하고 있습니다.', PUBLISHED: 'IR 신호 전송을 요청했습니다.',
    SUCCEEDED: 'IR 신호 전송 완료', FAILED: 'IR 신호 전송에 실패했습니다.',
    TIMED_OUT: 'IR 신호 전송 확인 시간이 초과되었습니다.', REJECTED: 'IR 전송 요청이 거부되었습니다.'
};
export function formatAppMetric(value) {
    return typeof value === 'number' && Number.isFinite(value) ? value.toFixed(1) : '—';
}
export function readingsQuery(days, now = new Date()) {
    const span = [1, 7, 30].includes(days) ? days : 1;
    const kst = (date) => new Date(date.getTime() + 9 * 3600000).toISOString().slice(0, 19) + '+09:00';
    return { from: kst(new Date(now.getTime() - span * 86400000)), to: kst(now), resolution: span === 1 ? 'raw' : 'hourly' };
}
export function validateClaim(deviceId) {
    const device_id = typeof deviceId === 'string' ? deviceId.trim() : '';
    if (!/^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/.test(device_id)) {
        throw new Error('장치 ID를 확인해 주세요.');
    }
    return { device_id };
}
export function parseClaimQr(raw) {
    try {
        const text = typeof raw === 'string' ? raw.trim() : '';
        if (/^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/.test(text)) return validateClaim(text);
        const url = new URL(text);
        const keys = [...url.searchParams.keys()];
        if (url.protocol !== 'airconrg:' || url.hostname.toLowerCase() !== 'iot' || url.pathname !== '/claim'
            || text.includes('#') || url.username || url.password || url.port
            || !keys.every((key) => ['v', 'device_id', 'code'].includes(key))
            || !['v', 'device_id'].every((key) => url.searchParams.getAll(key).length === 1)
            || url.searchParams.getAll('code').length > 1
            || url.searchParams.get('v') !== '1') throw new Error();
        return validateClaim(url.searchParams.get('device_id'));
    } catch (_) { throw new Error('IoT 등록 QR 형식을 확인해 주세요.'); }
}
export function irUnavailableReason(control) {
    if (!control) return 'IR 제어 정보를 확인할 수 없습니다. 센서 정보는 계속 확인할 수 있습니다.';
    if (control.capability?.enabled !== true) return '이 장치는 IR 제어를 지원하지 않습니다.';
    if (control.capability?.protocol_version !== 1) return '이 장치의 IR 제어 버전을 지원하지 않습니다.';
    if (control.connection?.telemetry_state !== 'ONLINE') return '장치가 온라인일 때 IR 제어를 사용할 수 있습니다.';
    if (control.connection?.control_ready !== true) return '장치의 IR 제어 준비가 완료되지 않았습니다.';
    if (control.active_command_id) return '이전 IR 전송 결과를 확인하는 중입니다.';
    return null;
}
export function commandKey() {
    if (window.crypto.randomUUID) return window.crypto.randomUUID();
    const bytes = window.crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
function safeError(status, path) {
    if (status === 401) return ['/api/app/auth/session', '/api/auth/login'].includes(path) ? '아이디 또는 비밀번호를 확인해 주세요.' : '로그인이 만료되었습니다. 다시 로그인해 주세요.';
    if (status === 403) return '고객 계정의 접근 권한이 필요합니다.';
    if (status === 404) return '접근 가능한 장치 또는 요청 정보를 찾을 수 없습니다.';
    if (status === 409) return '이미 등록되었거나 진행 중인 요청입니다. 새로고침 후 확인해 주세요.';
    if (status === 400) return '입력한 정보를 확인해 주세요.';
    if (status === 503) return '서비스를 사용할 수 없습니다. 잠시 후 다시 시도해 주세요.';
    return '요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.';
}
export function createIotAppClient(onUnauthorized, fetchImpl = (...args) => fetch(...args)) {
    let accessToken = null;
    let sessionKind = 'customer';
    let generation = 0;
    const pending = new Set();
    function clear() {
        generation += 1;
        accessToken = null;
        sessionKind = 'customer';
        pending.forEach((controller) => controller.abort());
        pending.clear();
    }
    async function request(path, { method = 'GET', body, signal, headers = {}, anonymous = false, command = false } = {}) {
        const requestGeneration = generation;
        if (!anonymous && !accessToken) throw new Error('고객 계정으로 로그인해 주세요.');
        const staffCommand = method === 'POST' && /^\/api\/admin\/iot\/devices\/[^/]+\/ir-control\/commands$/.test(path);
        if (!anonymous && sessionKind === 'staff' && (path.startsWith('/api/app/') || (method !== 'GET' && path !== '/api/auth/logout' && !staffCommand))) throw new Error('고객 계정에서 사용할 수 있는 기능입니다.');
        const controller = new AbortController();
        const cancel = () => controller.abort();
        if (signal?.aborted) controller.abort();
        signal?.addEventListener('abort', cancel, { once: true });
        pending.add(controller);
        const timer = setTimeout(cancel, 15000);
        try {
            const response = await fetchImpl(path, {
                method, signal: controller.signal, credentials: 'omit', cache: 'no-store',
                headers: { ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
                    ...(!anonymous ? { Authorization: `Bearer ${accessToken}` } : {}), ...headers },
                ...(body === undefined ? {} : { body: JSON.stringify(body) })
            });
            if (requestGeneration !== generation || controller.signal.aborted) throw new DOMException('Canceled', 'AbortError');
            const data = await response.json();
            if (requestGeneration !== generation || controller.signal.aborted) throw new DOMException('Canceled', 'AbortError');
            if (response.status === 401 && !anonymous) { clear(); onUnauthorized(); }
            if (command && data.command && [409, 503].includes(response.status)) return data;
            if (!response.ok || data.success !== true) {
                const error = new Error(safeError(response.status, path));
                error.status = response.status;
                throw error;
            }
            return data;
        } catch (error) {
            if (signal?.aborted || requestGeneration !== generation) throw new DOMException('Canceled', 'AbortError');
            if (error.status) throw error;
            // Never surface response bodies, claim codes or credentials in errors/logs.
            throw new Error('서버에 연결하지 못했습니다. 연결 상태를 확인하고 다시 시도해 주세요.');
        } finally {
            clearTimeout(timer);
            signal?.removeEventListener('abort', cancel);
            pending.delete(controller);
        }
    }
    const devicePath = (id) => `/api/${sessionKind === 'staff' ? 'admin' : 'app'}/iot/devices/${encodeURIComponent(id)}`;
    return {
        clear,
        async login(email, password) {
            clear();
            const data = await request('/api/app/auth/session', { method: 'POST', anonymous: true, body: { email_id: email.trim(), user_pw: password } });
            if (data.principal_type !== 'USER' || !data.access_token || !Number.isFinite(Date.parse(data.expires_at))) {
                throw new Error('고객 로그인 정보를 확인할 수 없습니다.');
            }
            accessToken = data.access_token;
            return { userId: data.user_id, expiresAt: data.expires_at };
        },
        async loginStaff(id, password) {
            clear();
            const data = await request('/api/auth/login', { method: 'POST', anonymous: true, body: { id: id.trim(), pw: password } });
            if (!['staff', 'admin'].includes(data.user?.role) || !data.token || !data.user.id) throw new Error('직원 로그인 정보를 확인할 수 없습니다.');
            accessToken = data.token;
            sessionKind = 'staff';
            return { kind: 'staff', userId: data.user.id, expiresAt: new Date(Date.now() + 86400000).toISOString() };
        },
        async logout() {
            try { await request(sessionKind === 'staff' ? '/api/auth/logout' : '/api/app/auth/session', { method: sessionKind === 'staff' ? 'POST' : 'DELETE' }); }
            finally { clear(); }
        },
        devices: (signal) => request(`/api/${sessionKind === 'staff' ? 'admin' : 'app'}/iot/devices`, { signal }),
        incoming: (signal) => request('/api/app/iot/invitations', { signal }),
        device: (id, signal) => request(devicePath(id), { signal }),
        async readings(id, days, signal, metric = 'pm2_5') {
            const staff = sessionKind === 'staff';
            const data = await request(`${devicePath(id)}/readings?${new URLSearchParams({ ...readingsQuery(days), ...(staff ? { metric } : {}) })}`, { signal });
            return staff ? { ...data, series: { [metric]: data.points } } : data;
        },
        control: (id, signal) => request(`${devicePath(id)}/ir-control`, { signal }),
        send: (id, state, key, signal) => request(`${devicePath(id)}/ir-control/commands`, { method: 'POST', body: state, headers: { 'Idempotency-Key': key }, command: true, signal }),
        command: (id, commandId, signal) => request(`${devicePath(id)}/ir-control/commands/${encodeURIComponent(commandId)}`, { signal }),
        claim: (data) => request('/api/app/iot/devices/claim', { method: 'POST', body: data }),
        metadata: (id, data) => request(devicePath(id), { method: 'PATCH', body: data }),
        release: (id) => request(`${devicePath(id)}/claim`, { method: 'DELETE', body: { confirm: true } }),
        leave: (id) => request(`${devicePath(id)}/membership`, { method: 'DELETE' }),
        members: (id, signal) => request(`${devicePath(id)}/members`, { signal }),
        invitations: (id, signal) => request(`${devicePath(id)}/invitations`, { signal }),
        invite: (id, email) => request(`${devicePath(id)}/invitations`, { method: 'POST', body: { email: email.trim() } }),
        respond: (id, accepted) => request(`/api/app/iot/invitations/${encodeURIComponent(id)}/${accepted ? 'accept' : 'reject'}`, { method: 'POST' }),
        cancelInvitation: (id, invitationId) => request(`${devicePath(id)}/invitations/${encodeURIComponent(invitationId)}`, { method: 'DELETE' }),
        removeMember: (id, email) => request(`${devicePath(id)}/members/${encodeURIComponent(email)}`, { method: 'DELETE' })
    };
}
